import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styles from "./customer-passcode-signin.module.scss";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "../../../../utils/error-handler";
import { toast } from "react-toastify";
import PasscodeOTPInput from "../components/passcode-otp-input/PasscodeOTPInput";
import { getSigninWithPasscodeConfig } from "./util";
import { useRequestPasscodeMutation, useVerifyPasscodeMutation } from "../../../../service/authentication-service";
import { ApplicationRoutes } from "../../../../routes";
import { useDispatch } from "react-redux";
import { logIn } from "../../../../stores/slices/current-user-slice";
import { setBroadcastOnLogin } from "../../../../stores/slices/broadcast-notification-slice";
import { AuthPageLayout } from "../../components";
import { FormWrapper } from "../components";
import { InfoIcon, LandingEmailIcon,  PhoneIconOTP } from "../../../../assets/icons";
import Input from "../../../../common/ekedc-components/input/Input";
import Button from "../../../../common/ekedc-components/button/Button";
import { UserType } from "../../../../model/userType";
import { validatePhoneNumber } from "../../validators";
import PhoneInput from "../../../../common/ekedc-components/phone-number-input/PhoneInput";
import { getUserIdFromToken } from "../../../../guards/common/authentification-functions";
import { useTenant } from "../../../../tenant/branding/useTenant";
import {
	describeError,
	OtpChannel,
	trackLoginFailed,
	trackLoginOtpResent,
	trackLoginOtpSent,
	trackLoginStarted,
	trackLoginSuccess,
} from "../../../../services/business-analytics/authEvents";

type OtpMethod = "email" | "phone";

interface FormData {
	email: string;
	phoneNumber: string;
}

export const CustomerPassCodeLoginPage = () => {
	const { t } = useTranslation();
	const { countryCode } = useTenant();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [currentStep, setCurrentStep] = useState(1);
	const [passcode, setPasscode] = useState("");
	const [verifyPasscode, { isLoading: isVerifyingPasscode }] = useVerifyPasscodeMutation();
	const [requestPasscode, { isLoading: isRequestingPasscode }] = useRequestPasscodeMutation();
	const [userId, setUserId] = useState("");
	const [otpMethod, setOtpMethod] = useState<OtpMethod>("email");
	const [serverWarning, setServerWarning] = useState<{ email?: string; phoneNumber?: string }>({});
	const [dialCode, setDialCode] = useState("");
	const resendCount = useRef(0);
	const otpChannel: OtpChannel = otpMethod === "email" ? "email" : "sms";

	useEffect(() => {
		trackLoginStarted("otp");
	}, []);

	const {
		control,
		handleSubmit,
		watch,
		reset,
		formState: { errors },
	} = useForm<FormData>({
		mode: "onTouched",
		defaultValues: {
			email: "",
			phoneNumber: ""
		},
	});
	const email = watch("email");
	const phoneNumber = watch('phoneNumber');
	const identifier = otpMethod === "email" ? email : phoneNumber;
	const isIdentifierFilled = otpMethod === "email" ? !!email : phoneNumber.length > 4;
	const signWithPasscodeConfig = getSigninWithPasscodeConfig(currentStep, identifier);

	const handleGoBack = () => {
		if (currentStep === 1) {
			navigate(-1);
		} else {
			setCurrentStep(currentStep - 1);
		}
	};

	const handleOTPSubmit = async () => {
		try {
			const response = await verifyPasscode({ passcode, userId }).unwrap();
			console.log(response.userType)
			if (response.userType != UserType.Customer) {
				trackLoginFailed("otp", "not_registered_as_customer");
				toast.warning(t("auth.web.notRegisteredAsCustomer"));
				reset();
				setPasscode("");
				setUserId("");
			} else {
				dispatch(logIn({ token: response.token, refreshToken: response.refreshToken, userType: UserType.Customer }));
				trackLoginSuccess("otp", getUserIdFromToken(response.token));
				dispatch(setBroadcastOnLogin(true));
				navigate("/dashboard");
			}

		} catch (err: any) {
			trackLoginFailed("otp", describeError(err));
			toast.error(getErrorMessage(err?.data));
			reset();
			setPasscode("");
			setUserId("");
		}
	};

	const handleResendOTP = async () => {
		const response = await requestPasscode({ identifier }).unwrap();
		setUserId(response.userId);
		resendCount.current += 1;
		trackLoginOtpResent(otpChannel, resendCount.current);
		toast.success(t("auth.web.passcodeResentSuccessful"));
	};

	const isZeroId = (id: string) => /^0+-0+-0+-0+-0+$/.test(id);

	const handleOnSubmit = async (data: FormData) => {
		if (userId) {
			await handleOTPSubmit();
			return;
		}
		setServerWarning({});
		if (otpMethod === "phone") {
			try {
				const response = await requestPasscode({ identifier: data.phoneNumber }).unwrap();
				if (isZeroId(response.userId)) {
					setServerWarning({ phoneNumber: t("auth.web.unableToSendCodePhone") });
					return;
				}
				setUserId(response.userId);
				resendCount.current = 0;
				trackLoginOtpSent("sms", data.phoneNumber, dialCode);
			} catch (err: any) {
				toast.error(getErrorMessage(err?.data));
			}
			return;
		}
		try {
			const response = await requestPasscode({ identifier: data.email }).unwrap();
			if (isZeroId(response.userId)) {
				setServerWarning({ email: t("auth.web.unableToSendCodeEmail") });
				return;
			}
			setUserId(response.userId);
			resendCount.current = 0;
			trackLoginOtpSent("email", data.email);
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data));
		}
	};

	const handleLoginWithPassCode = () => {
		navigate(`../${ApplicationRoutes.CUSTOMER_SIGNIN}`);
	};

	const renderStep1 = () => (
		<form onSubmit={e => e.preventDefault()} className={styles.loginForm}>
			<div className={styles.methodSelection}>
				<div className={styles.methodSelectionHeader}>
					<span className="">{t("auth.chooseHowToReceiveYourCode")} </span>
					<InfoIcon />
				</div>
				<div className={styles.optionsContainer}>

					<div
						className={`${styles.methodOption} ${otpMethod === "email" ? styles.methodOptionActive : ""}`}
						onClick={() => {
							setOtpMethod("email");
							setUserId("");
							setPasscode("");
							setServerWarning({});
							reset();
						}}
					>
						<div className={`${styles.optionIconContainer} ${otpMethod === "email" ? styles.methodOptionActiveIcon : ""}`}>
							<LandingEmailIcon className={styles.optionIcon} />

						</div>
						<span>{t("common.labels.emailAddress")}</span>
					</div>
					<div className={styles.optionDivider} />
					<div
						className={`${styles.methodOption} ${otpMethod === "phone" ? styles.methodOptionActive : ""}`}
						onClick={() => {
							setOtpMethod("phone");
							setUserId("");
							setPasscode("");
							setServerWarning({});
							reset();
						}}
					>
						<div className={`${styles.optionIconContainer} ${otpMethod === "phone" ? styles.methodOptionActiveIcon : ""}`}>
							<PhoneIconOTP className={styles.optionIcon} />

						</div>
						<span>{t("common.labels.phoneNumber")}</span>
					</div>
				</div>

			</div>

			<div className={styles.formInputContainer}>
				{userId ? (
					<div className={styles.verifiedMessage}>
						<span className={styles.verifiedText}>
							{otpMethod === "email" ? t("auth.emailAddressVerified") : t("auth.phoneNumberVerified")}
						</span>
					</div>
				) : otpMethod === "email" ? (
					<>
						<div className={styles.methodSelectionHeader}>
							<span>{t("auth.resetPasswordInitialTitle")}</span>
							<InfoIcon />
						</div>
						<Controller
							name="email"
							control={control}
							rules={{
								required: t("auth.web.emailIsRequired"),
								pattern: {
									value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
									message: t("auth.web.invalidEmailAddress"),
								},
							}}
							render={({ field }) => (
								<Input type="email" placeholder={t("auth.web.registeredEmailPlaceholder")} value={field.value} onChange={(e) => { setServerWarning(prev => ({ ...prev, email: undefined })); field.onChange(e); }} name="email" error={errors.email?.message} warning={serverWarning.email} loading={isRequestingPasscode} loadingText={t("common.labels.inputLoadingLabel")} />
							)}
						/>
					</>
				) : (
					<>
					
					<div className={styles.methodSelectionHeader}>
							<span>{t("auth.provideYourRegisteredPhoneNumber")}</span>
							<InfoIcon />
						</div>
					<Controller name="phoneNumber" control={control}
						rules={{
							validate: (val) => {
								if (otpMethod != 'phone') {
									return true;
								}
								if(!val) return t("errors.api.phoneNumberRequired");
								return validatePhoneNumber(val);
							}
						}}
						render={({field}) => (<PhoneInput
						label=""
							value={field.value}
							onChangeParts={(val, dialCode) => { setServerWarning(prev => ({ ...prev, phoneNumber: undefined })); field.onChange(val); setDialCode(dialCode)}}
							onBlur={field.onBlur}
							placeholder={isRequestingPasscode ? t("auth.web.sendingYourCode") : t("auth.web.enterPhoneNumber")}
							error={errors.phoneNumber?.message}
							warning={serverWarning.phoneNumber}
							disabled={isRequestingPasscode}
							showCountrySelector={false}
							defaultCountry={countryCode}
							lockCountry
							inputSize="big"
						/>)}/>

						</>
						

				)}
			</div>


			{userId && (
				<PasscodeOTPInput
					value={passcode}
					onChange={setPasscode}
					onResend={handleResendOTP}
					otpLength={6}
					resendTimerSeconds={180}
					disabled={isVerifyingPasscode}
					error={false}
					sendToEmail={otpMethod == 'email'}
					contact={otpMethod == 'email' ? email : phoneNumber}
					dialCode={dialCode}
					renderSeparator={otpMethod == 'email'}
				/>
			)}

			<div className={styles.buttonSection}>
				<Button
					text={t("common.actions.continueKey")}
					loading={isRequestingPasscode}
					loadingText={t("auth.web.sending")}
					onClick={() => handleSubmit(handleOnSubmit)()}
					disabled={!userId ? !isIdentifierFilled : passcode.length < 6}
				/>
			</div>
		</form>
	);



	return (
		<AuthPageLayout>
			<FormWrapper
				currentStep={0}
				totalSteps={0}
				onBack={handleGoBack}
				showProgress={false}
				title={signWithPasscodeConfig.title}
				subtitle={signWithPasscodeConfig.subtitle}
			>
				{renderStep1()}
			</FormWrapper>
		</AuthPageLayout>
	);
};
